import { useState } from "react";
import { questions } from "../data/questions";
import { reactions } from "../data/reactions";
import { ProgressBar } from "./ProgressBar";
import { QuestionCard } from "./QuestionCard";
import { ReactionSplash } from "./ReactionSplash";
import { LogoFull } from "./Logo";
import { trackEvent } from "../lib/analytics";

interface QuizScreenProps {
  currentIndex: number;
  answers: number[];
  onAnswerChange: (questionIdx: number, answerIdx: number) => void;
  onIndexChange: (newIdx: number) => void;
  onComplete: (finalAnswers: number[]) => void;
}

export function QuizScreen({
  currentIndex,
  answers,
  onAnswerChange,
  onIndexChange,
  onComplete,
}: QuizScreenProps) {
  const [showSplash, setShowSplash] = useState<string | null>(null);
  const total = questions.length;
  const question = questions[currentIndex];

  const handleAnswer = (answerIdx: number) => {
    onAnswerChange(currentIndex, answerIdx);
    trackEvent("quiz_answer", { question: question.id, answer: answerIdx });

    const isLast = currentIndex === total - 1;
    if (isLast) {
      const finalAnswers = [...answers];
      finalAnswers[currentIndex] = answerIdx;
      onComplete(finalAnswers);
      return;
    }

    // Splash de réaction (clé = id de la question qu'on vient de répondre)
    const reaction = reactions[question.id];
    if (reaction) {
      setShowSplash(reaction);
      window.setTimeout(() => {
        setShowSplash(null);
        onIndexChange(currentIndex + 1);
      }, 1200);
    } else {
      onIndexChange(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex === 0) return;
    onIndexChange(currentIndex - 1);
  };

  return (
    <section className="relative min-h-[100svh] bg-background">
      <div className="max-w-md mx-auto px-6 py-8">
        {/* Logo top */}
        <div className="flex justify-center mb-6 opacity-80">
          <LogoFull />
        </div>

        <ProgressBar current={currentIndex + 1} total={total} />

        <div className="mt-4 min-h-[6px]">
          {currentIndex > 0 && (
            <button
              type="button"
              onClick={handlePrev}
              className="font-heading text-sm text-primary hover:underline no-tap-highlight"
            >
              ← Question précédente
            </button>
          )}
        </div>

        <div className="mt-6">
          <QuestionCard
            key={question.id}
            question={question}
            initialAnswer={answers[currentIndex]}
            onAnswer={handleAnswer}
          />
        </div>
      </div>

      {showSplash && <ReactionSplash text={showSplash} />}
    </section>
  );
}
